// @flow

import * as React from 'react';

import type {
    CalculationRequestProjectResult,
} from "../common/RequestTypes";

import OptionManager from "../common/OptionManager";
import type {ProjectUiInput} from "../store/Calculation";
import CalculationSectionSubHeader from "./CalculationSectionSubHeader";
import type {PhaseState} from "./CalculationPhaseInput";
import ReactAI from 'react-appinsights';

type Props = {
    calcResult: CalculationRequestProjectResult,
    projectUiInput: ProjectUiInput,
    optionManager: OptionManager
};


class ProjectInfoTable extends React.Component<Props> {
    
    techPrefixes: string[];
    techNames: string[];
    
    constructor() {
        super();
        this.techPrefixes = ['ifl', 'tc', 'dd', 'sl', 'pul'];
        this.techNames = ['Flexibla foder', 'Rörspräckning', 'Styrd borrning', 'Sliplining', 'PU liner'];
    }

    render() {

        let multiPhase = this.props.projectUiInput.phaseStates.length > 1;

        return (
            <div>
                <div className="row">
                    <div className="col-sm-12 col-md-6">
                        <span className="project-info-label">Rapport skapad: </span>{ new Date().toLocaleString() }
                    </div>
                    <div className="col-sm-12 col-md-6">
                        <span className="project-info-label">Organisation: </span>{ this.props.projectUiInput.organisationName }
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12 col-md-6">
                        <span className="project-info-label">Projektnamn: </span>{ this.props.projectUiInput.projectName }
                    </div>
                    <div className="col-sm-12 col-md-6">
                        <span className="project-info-label">Namn: </span>{ this.props.projectUiInput.personName }
                    </div>
                </div>

                <CalculationSectionSubHeader text="Platsen" />
                <table className="table table-borderless table-customstriped table-sm">
                    <thead>
                    <tr>
                        { multiPhase && <th>Etapp</th> }
                        <th>Landskap före</th>
                        <th>Beläggning före</th>
                        <th>Landskap efter</th>
                        <th>Beläggning efter</th>
                    </tr>
                    </thead>
                    <tbody>
                        {this.props.projectUiInput.phaseStates.map((phaseState, idx) => (<tr key={idx}>
                            { multiPhase && <td>{ idx + 1 }</td> }
                            <td>{ this.getLandscapeBeforeText(phaseState) }</td>
                            <td>{ this.getCoatingBeforeText(phaseState) }</td>
                            <td>{ this.getLandscapeAfterText(phaseState) }</td>
                            <td>{ this.getCoatingAfterText(phaseState) }</td>
                        </tr>))}
                    </tbody>
                </table>

                <CalculationSectionSubHeader text="Rör NoDig" />
                <table className="table table-borderless table-customstriped table-sm">
                    <thead>
                    <tr>
                        { multiPhase && <th>Etapp</th> }
                        <th>Typ</th>
                        <th>Teknik</th>
                        <th>Dia före (mm)</th>
                        <th>Material före</th>
                        <th>Dia efter (mm)</th>
                        <th>Material efter</th>
                        <th>Längd (m)</th>
                    </tr>
                    </thead>
                    <tbody>
                        
                        { this.props.projectUiInput.phaseStates.map((phaseState, idx) =>
                            this.getTubeRows(phaseState, idx + 1, this.props.projectUiInput.phaseStates.length))    
                            .reduce((previous, current) => [...previous, ...current], []) 
                        }                          
                    </tbody>
                </table>

                <CalculationSectionSubHeader text="Rör Traditionell schakt" />
                <table className="table table-borderless table-customstriped table-sm">
                    <thead>
                    <tr>
                        { multiPhase && <th>Etapp</th> }
                        <th>Typ</th>
                        <th>Dia före (mm)</th>
                        <th>Material före</th>
                        <th>Dia efter (mm)</th>
                        <th>Material efter</th>
                        <th>Längd (m)</th>
                    </tr>
                    </thead>
                    <tbody>
                        
                        { this.props.projectUiInput.phaseStates.map((phaseState, idx) =>
                            this.getTubeRowsTrad(phaseState, idx + 1, this.props.projectUiInput.phaseStates.length))    
                            .reduce((previous, current) => [...previous, ...current], []) 
                        }                          
                    </tbody>
                </table>



            </div>
        );

    }
    
    getLandscapeBeforeText(phaseState: PhaseState) {
        let om = this.props.optionManager;
        let sel = this.props.optionManager.availableSelections;
        let retVal = om.getTextValue(phaseState.typeOfLandscapeBefore, sel.typeOfLandscape);
        if(phaseState.typeOfLandscapeBefore === om.ids.TYPE_OF_LANDSCAPE_STREET)
            retVal += `, ${om.getTextValue(phaseState.streetTypeBefore, sel.streetType)}`;
        return retVal;
    }
    
    getLandscapeAfterText(phaseState: PhaseState) {
        let om = this.props.optionManager;
        let sel = this.props.optionManager.availableSelections;
        let retVal = om.getTextValue(phaseState.typeOfLandscapeAfter, sel.typeOfLandscape);
        if(phaseState.typeOfLandscapeAfter === om.ids.TYPE_OF_LANDSCAPE_STREET)
            retVal += `, ${om.getTextValue(phaseState.streetTypeAfter, sel.streetType)}`;
        return retVal;
    }
    
    getCoatingBeforeText(phaseState: PhaseState) {
        let om = this.props.optionManager;
        let sel = this.props.optionManager.availableSelections;
        let retVal = om.getTextValue(phaseState.coatingBefore, sel.coating);
        return retVal;
    }

    getCoatingAfterText(phaseState: PhaseState) {
        let om = this.props.optionManager;
        let sel = this.props.optionManager.availableSelections;
        let retVal = om.getTextValue(phaseState.coatingAfter, sel.coating);
        if(phaseState.coatingAfter === om.ids.COATING_ASPHALT)
            retVal += `, ${om.getTextValue(phaseState.asphaltTypeAfter, sel.asphaltType)}`;
        return retVal;
    }
    
    getTubeRows(phaseState: PhaseState, phaseNr: number, numPhases: number) {
        return [
            ...this.getTubeRowsTech(phaseState, 'ifl', 2, phaseNr, numPhases),
            ...this.getTubeRowsTech(phaseState, 'tc', 3, phaseNr, numPhases),
            ...this.getTubeRowsTech(phaseState, 'dd', 3, phaseNr, numPhases),
            ...this.getTubeRowsTech(phaseState, 'sl', 1, phaseNr, numPhases),
            ...this.getTubeRowsTech(phaseState, 'pul', 1, phaseNr, numPhases),
        ]; 
    }
    
    getTubeRowsTrad(phaseState: PhaseState, phaseNr: number, numPhases: number) {
        return this.getTubeRowsTechTrad(phaseState, 3, phaseNr, numPhases);
    }
    
    getTubeRowsTech(phaseState: PhaseState, prefix: string, numTubes: number, phaseNr: number, numPhases: number) {
        let rows = [];
        let om = this.props.optionManager;
        let sel = this.props.optionManager.availableSelections;
        let techName = this.techNames[this.techPrefixes.indexOf(prefix)];
        let multiPhase = numPhases > 1;
        for(let tubeNr = 1; tubeNr <= numTubes; tubeNr++) {
            let noDigTubeType = phaseState[`${prefix}Tube${tubeNr}TubeType`];
            if(typeof(noDigTubeType) === 'number' && noDigTubeType !== -1) {
               let matchingExistingTubeNr = this.getExistingTubeWithTubeType(phaseState, noDigTubeType);
               
               let tubeMaterial = phaseState[`${prefix}Tube${tubeNr}TubeMaterial`] ? om.getTextValue(phaseState[`${prefix}Tube${tubeNr}TubeMaterial`], sel.tubeMaterialAll) : '';
               if(prefix === 'ifl')
                   tubeMaterial = "Armerat foder";
               if(prefix === 'pul')
                   tubeMaterial = "PU liner";
               if(prefix === 'sl' && tubeMaterial === '') // No material for sliplining now but will have material option later
                   tubeMaterial = 'PE';
               
               let row = (<tr key={phaseNr + prefix + '_' + tubeNr}>
                   { multiPhase && <td>{ phaseNr }</td> }
                   <td>{ om.getTextValue(noDigTubeType, sel.tubeType) }</td>
                   <td>{ techName }</td>
                   <td>{ matchingExistingTubeNr !== -1 ? om.getTextValue(phaseState[`existingTube${matchingExistingTubeNr}TubeDiameter`], sel.tubeDiameterAll) : '' }</td>
                   <td>{ matchingExistingTubeNr !== -1 ? om.getTextValue(phaseState[`existingTube${matchingExistingTubeNr}TubeMaterial`], sel.tubeMaterialAll) : '' }</td>
                   <td>{ om.getTextValue(phaseState[`${prefix}Tube${tubeNr}TubeDiameter`], sel.tubeDiameterAll) }</td>
                   <td>{ tubeMaterial }</td>
                   <td>{ phaseState[`${prefix}Tube${tubeNr}TubeLength`] }</td>
               </tr>);
                rows.push(row);
            }
        }
        return rows;
    }
    
    getTubeRowsTechTrad(phaseState: PhaseState, numTubes: number, phaseNr: number, numPhases: number) {
        let rows = [];
        let om = this.props.optionManager;
        let sel = this.props.optionManager.availableSelections;
        let prefix = 'traditional';
        let multiPhase = numPhases > 1;
        for(let tubeNr = 1; tubeNr <= numTubes; tubeNr++) {
            let tradTubeType = phaseState[`${prefix}Tube${tubeNr}TubeType`];
            if(typeof(tradTubeType) === 'number' && tradTubeType !== -1) {
               let matchingExistingTubeNr = this.getExistingTubeWithTubeType(phaseState, tradTubeType);
               
               let tubeMaterial = phaseState[`${prefix}Tube${tubeNr}TubeMaterial`] ? om.getTextValue(phaseState[`${prefix}Tube${tubeNr}TubeMaterial`], sel.tubeMaterialAll) : '';
               
               let row = (<tr key={phaseNr + prefix + '_' + tubeNr}>
                   { multiPhase && <td>{ phaseNr }</td> }
                   <td>{ om.getTextValue(tradTubeType, sel.tubeType) }</td>
                   <td>{ matchingExistingTubeNr !== -1 ? om.getTextValue(phaseState[`existingTube${matchingExistingTubeNr}TubeDiameter`], sel.tubeDiameterAll) : '' }</td>
                   <td>{ matchingExistingTubeNr !== -1 ? om.getTextValue(phaseState[`existingTube${matchingExistingTubeNr}TubeMaterial`], sel.tubeMaterialAll) : '' }</td>
                   <td>{ om.getTextValue(phaseState[`${prefix}Tube${tubeNr}TubeDiameter`], sel.tubeDiameterAll) }</td>
                   <td>{ tubeMaterial }</td>
                   <td>{ phaseState[`${prefix}Tube${tubeNr}TubeLength`] }</td>
               </tr>);
                rows.push(row);
            }
        }
        return rows;
    }
    
    getExistingTubeWithTubeType(phaseState: PhaseState, tubeType: number) {
        for(let tubeNr = 1; tubeNr <= 3; tubeNr++) {
            if(phaseState[`existingTube${tubeNr}TubeType`] === tubeType) {
                return tubeNr;
            }
        }
        return -1;
    }
    
}

export default ReactAI.withTracking(ProjectInfoTable); 






// WEBPACK FOOTER //
// ./src/components/ProjectInfoTable.js